import React, { useState, useEffect, useCallback, useRef } from 'react';
import { RefreshCw, AlertTriangle, CheckCircle2, FileSearch, ShieldAlert, FileText, ExternalLink, Shuffle } from 'lucide-react';
import './TestEntryAudit.css';

const SF_BASE = 'https://iteach.my.salesforce.com';

const SEVERITY_COLOR = { error: '#EF4444', warn: '#FBBF24', info: '#60A5FA' };
const SEVERITY_LABEL = { error: 'Error', warn: 'Warning', info: 'Note' };

const VERDICT = {
  match: { color: '#4ADE80', label: 'Matches the report' },
  mismatch: { color: '#EF4444', label: 'Disagrees with the report' },
  not_in_document: { color: '#FBBF24', label: 'Not found in the report' },
  not_entered: { color: '#FBBF24', label: 'Report has a score, none recorded' },
  no_document: { color: '#94A3B8', label: 'No report on file' },
  no_score_in_report: { color: '#94A3B8', label: 'Report prints no score' },
  partial: { color: '#94A3B8', label: 'Only part of the report was read' },
  date_unconfirmed: { color: '#94A3B8', label: 'Date could not be confirmed' },
  unreadable: { color: '#94A3B8', label: 'Report unreadable' },
  no_results: { color: '#94A3B8', label: 'No results in report' },
  error: { color: '#94A3B8', label: 'Could not check' },
};

// Verdicts that say nothing about whether a person typed the right thing —
// the document was missing, unreadable, or only partly seen. Excluded from the
// accuracy denominator rather than counted as failures.
const INCONCLUSIVE = new Set(['unreadable', 'no_results', 'no_document', 'error', 'not_in_document', 'no_score_in_report', 'partial', 'date_unconfirmed']);

// Nothing was recorded against the test at all. That is a real problem, but it
// is a gap rather than a misreading, and mixing the two makes the accuracy
// number answer neither question. Counted and reported separately.
const NOT_RECORDED = 'not_entered';

// How many records each run walks, and how many requests are in flight. Each
// call is one record, so concurrency is about wall-clock, not timeouts.
const RUN_SIZE = { sample: 20, suspect: 10, all: 150 };
const CONCURRENCY = 3;

// Below this many verifiable records, Run audit reads every report by itself.
// A day's entries are a handful, and for a daily check nobody should have to
// press a second button. Above it the reading is offered rather than assumed,
// because a long window is a great many documents and real money.
const AUTO_VERIFY_MAX = 40;

// Teams are a roster plus the state they work, keyed by display name. Selecting
// one filters the audit to those people — records they created OR scores they
// typed — and pins every member onto the scorecard even on a day they entered
// nothing, so a supervisor sees the whole team at a glance. Add a team here.
const TEAMS = {
  'Louisiana Team': {
    state: 'Louisiana',
    supervisor: 'Todd Johnson',
    members: ['Tracy Miller', 'Tara Bills', 'Laura Johnston'],
  },
};
const TEAM_STORAGE_KEY = 'testaudit_team';

// The signed-in user, so a stored verdict can say who ran it. Read from the
// same session the rest of the dashboard keeps; absent or malformed is fine.
const currentUserEmail = () => {
  try {
    return JSON.parse(localStorage.getItem('iteach_auth') || '{}').email || '';
  } catch {
    return '';
  }
};

const readStoredTeam = () => {
  try {
    const t = localStorage.getItem(TEAM_STORAGE_KEY);
    return t && TEAMS[t] ? t : '';
  } catch {
    return '';
  }
};

// Dates are ISO (YYYY-MM-DD) everywhere in the data — comparable, sortable,
// what Salesforce speaks — and MM-DD-YYYY everywhere a person reads them.
// Formatting at render time means stored verdicts written before this change
// display correctly too, without re-reading a single PDF.
const ISO_DATE = /\b(\d{4})-(\d{2})-(\d{2})\b/g;
const fmtDate = (v) => {
  if (!v) return '';
  const m = String(v).match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[2]}-${m[3]}-${m[1]}` : String(v);
};
const fmtDates = (text) => (text == null ? '' : String(text).replace(ISO_DATE, '$2-$3-$1'));
const fmtWhen = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return fmtDates(iso);
  const date = new Intl.DateTimeFormat('en-US', { timeZone: 'America/Chicago', month: '2-digit', day: '2-digit', year: 'numeric' })
    .format(d).replace(/\//g, '-');
  const time = new Intl.DateTimeFormat('en-US', { timeZone: 'America/Chicago', hour: 'numeric', minute: '2-digit' }).format(d);
  return `${date} ${time}`;
};

const card = {
  background: 'rgba(30, 27, 75, 0.55)',
  border: '1px solid rgba(129, 140, 248, 0.22)',
  borderRadius: '0.75rem',
  padding: '1.25rem',
};
const muted = { color: 'rgba(199, 210, 254, 0.7)' };

const Stat = ({ label, value, sub, tone }) => (
  <div style={{ ...card, flex: '1 1 150px', minWidth: 150 }}>
    <div style={{ ...muted, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</div>
    <div style={{ fontSize: '1.75rem', fontWeight: 600, color: tone || '#E0E7FF', marginTop: '0.35rem' }}>{value}</div>
    {sub && <div style={{ ...muted, fontSize: '0.75rem', marginTop: '0.2rem' }}>{sub}</div>}
  </div>
);

const TestEntryAudit = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [team, setTeam] = useState(readStoredTeam);
  const [days, setDays] = useState(1);
  const [state, setState] = useState(() => (readStoredTeam() ? TEAMS[readStoredTeam()].state : 'Louisiana'));
  const [enteredBy, setEnteredBy] = useState('');
  const [severityFilter, setSeverityFilter] = useState('all');
  const [personFilter, setPersonFilter] = useState('');

  // testId -> verified row, and which queue it came from.
  const [verified, setVerified] = useState({});
  const [run, setRun] = useState(null); // { mode, done, total }
  const cancelled = useRef(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    cancelled.current = true; // abandon any run against the previous window
    setRun(null);
    setVerified({});
    try {
      const params = new URLSearchParams({ days: String(days) });
      if (state !== 'All States') params.set('state', state);
      if (team && TEAMS[team]) params.set('people', TEAMS[team].members.join(','));
      else if (enteredBy.trim()) params.set('enteredBy', enteredBy.trim());

      const res = await fetch(`/.netlify/functions/test-entry-audit?${params}`);
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.error || `HTTP ${res.status}`);
      }
      const body = await res.json();
      // Start from every verdict already on record, so whoever opens the page
      // next sees the day's work as already checked rather than reading it all
      // again. Only what is new or has changed since gets read below.
      const seed = {};
      for (const r of body.checked || []) seed[r.testId] = { ...r, _queue: 'all' };
      setVerified(seed);
      setData(body);
    } catch (err) {
      console.error('Test entry audit failed:', err);
      setError(err.message);
    }
    setLoading(false);
  }, [days, state, enteredBy, team]);

  // Any change to what the page is looking at re-runs the audit, so the header,
  // scorecard and accuracy panel can never be drawn from different runs. Free-
  // text person search stays manual because it changes on every keystroke.
  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [days, state, team]);

  const chooseTeam = (name) => {
    setTeam(name);
    try { name ? localStorage.setItem(TEAM_STORAGE_KEY, name) : localStorage.removeItem(TEAM_STORAGE_KEY); } catch {}
    if (name && TEAMS[name]) {
      setState(TEAMS[name].state);
      setEnteredBy('');
    }
  };

  // Read every report automatically when the window is small enough to warrant
  // it. Guarded by a ref so re-renders during a run cannot start a second one.
  const autoRunFor = useRef(null);

  /** Walk a queue of test ids, one request per record, a few at a time. */
  const runVerification = useCallback(async (mode, { force = false } = {}) => {
    // A forced re-read walks everything with a report, cached or not — used
    // after the comparison logic changes or when a stored verdict looks wrong.
    const source = force
      ? [...new Set([...(data?.verifyQueue?.all || []), ...Object.keys(verified)])]
      : (data?.verifyQueue?.[mode] || []);
    const queue = source.slice(0, RUN_SIZE[mode]);
    if (!queue.length) return;
    const by = encodeURIComponent(currentUserEmail());

    cancelled.current = false;
    setRun({ mode, done: 0, total: queue.length });

    let cursor = 0;
    const next = async () => {
      while (cursor < queue.length && !cancelled.current) {
        const testId = queue[cursor++];
        try {
          const res = await fetch(`/.netlify/functions/test-entry-verify?testId=${testId}&by=${by}${force ? '&force=1' : ''}`);
          const body = await res.json();
          if (body.row) {
            setVerified((v) => ({ ...v, [testId]: { ...body.row, _queue: mode } }));
          }
        } catch (err) {
          setVerified((v) => ({
            ...v,
            [testId]: { testId, _queue: mode, pdfVerification: { status: 'error', detail: err.message } },
          }));
        }
        setRun((r) => (r ? { ...r, done: r.done + 1 } : r));
      }
    };

    await Promise.all(Array.from({ length: CONCURRENCY }, next));
    setRun((r) => (r ? { ...r, finished: true } : r));
  }, [data, verified]);

  useEffect(() => {
    if (!data) return;
    const queue = data.verifyQueue?.all || [];
    const key = `${data.window?.since}|${data.window?.state}|${data.window?.enteredBy}|${(data.window?.people || []).join(',')}|${queue.length}`;
    if (!queue.length || queue.length > AUTO_VERIFY_MAX) return;
    if (autoRunFor.current === key) return;
    autoRunFor.current = key;
    runVerification('all');
  }, [data, runVerification]);

  useEffect(() => () => { cancelled.current = true; }, []);

  const verifiedList = Object.values(verified);
  // 'all' and 'sample' are both unbiased with respect to the rules; 'suspect' is
  // not, and is reported separately so it can never inflate or deflate accuracy.
  const sampleResults = verifiedList.filter((r) => r._queue === 'all' || r._queue === 'sample');
  const suspectResults = verifiedList.filter((r) => r._queue === 'suspect');
  const isCensus = verifiedList.some((r) => r._queue === 'all');

  const accuracyOf = (list) => {
    const conclusive = list.filter((r) => r.pdfVerification && !INCONCLUSIVE.has(r.pdfVerification.status));
    const missing = conclusive.filter((r) => r.pdfVerification.status === NOT_RECORDED).length;
    const counted = conclusive.filter((r) => r.pdfVerification.status !== NOT_RECORDED);
    const matched = counted.filter((r) => r.pdfVerification.status === 'match').length;
    return {
      counted: counted.length,
      matched,
      missing,
      inconclusive: list.length - conclusive.length,
      pct: counted.length ? (100 * matched) / counted.length : null,
    };
  };
  const sampleAcc = accuracyOf(sampleResults);

  // Flagged rows from pass 1, plus anything the sample run turned up as wrong.
  const sampleProblems = sampleResults.filter(
    (r) => r.pdfVerification && !INCONCLUSIVE.has(r.pdfVerification.status) && r.pdfVerification.status !== 'match'
  );

  const rows = (data?.rows || [])
    .map((r) => (verified[r.testId] ? { ...r, pdfVerification: verified[r.testId].pdfVerification } : r))
    .filter((r) => {
      // Same credit rule as the scorecard: whoever set the score, else the creator.
      if (personFilter && (r.provenBy || r.enteredBy) !== personFilter) return false;
      if (severityFilter === 'all') return true;
      if (severityFilter === 'verified') return !!r.pdfVerification;
      return r.findings.some((f) => f.severity === severityFilter);
    });

  const busy = run && !run.finished;

  // Verdicts that mean the record is wrong against its report. A missing score
  // is included: the report shows a result and nothing was recorded.
  const REPORT_PROBLEM = new Set(['mismatch', NOT_RECORDED]);
  const creditOf = (r) => r.provenBy || r.enteredBy || '(unknown)';

  // The backend scorecard knows only the rules. Layer the report verdicts on top
  // so "clean" means clean on both, and a date that is provably wrong shows up
  // against the person who typed it rather than vanishing into 100%.
  const applyVerdicts = (list) => {
    const byName = new Map(list.map((p) => [p.name, { ...p, reportChecked: 0, reportProblems: 0, reportMatched: 0 }]));
    for (const r of verifiedList) {
      const v = r.pdfVerification;
      if (!v || INCONCLUSIVE.has(v.status)) continue;
      const p = byName.get(creditOf(r));
      if (!p) continue;
      p.reportChecked++;
      if (v.status === 'match') p.reportMatched++;
      if (REPORT_PROBLEM.has(v.status)) {
        p.reportProblems++;
        // Only demote from clean if the rules had passed it — otherwise it was
        // already counted against them.
        const cleanOnRules = !(r.findings || []).some((f) => f.severity !== 'info');
        if (cleanOnRules) p.clean = Math.max(0, p.clean - 1);
      }
    }
    return [...byName.values()].map((p) => ({
      ...p,
      cleanRate: p.records ? Number(((100 * p.clean) / p.records).toFixed(1)) : 0,
    }));
  };

  return (
    <div className="hf-container">
      {/* ── controls ── */}
      <div style={{ ...card, marginBottom: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.9rem' }}>
          <FileSearch size={18} style={{ color: '#818CF8' }} />
          <div style={{ fontSize: '1.05rem', fontWeight: 600, color: '#E0E7FF' }}>Test Entry Audit</div>
        </div>
        <details className="tea-help">
          <summary>How this works — what is in the window, and how records are attributed</summary>
          <div className="tea-help-body">
            <strong>What is in the window.</strong> Both records <em>created</em> in it and records whose score or
            date was <em>typed</em> in it — a Test record is created when a test is requested, so the score is
            usually filled in weeks later, and filtering on creation alone would miss the work actually done.
            <br /><br />
            <strong>Attribution.</strong> Field history was enabled on the score and date fields on
            2&nbsp;September&nbsp;2026. Records edited from that date carry a
            <strong> proven</strong> author — who set the value, and when. Records entered before it show the
            record's creator instead, which is an inference and <strong>not evidence of who typed a value</strong>:
            scores are usually filled in on a later edit, and 90.5% of scored records were last modified by
            somebody other than their creator.
            <br /><br />
            <strong>Dates.</strong> For Louisiana, <em>Date Taken</em> holds the report's issue date — LDOE treats it
            as the enrolled date. Other states accept either the test date or the report date until their
            convention is confirmed.
          </div>
        </details>

        <div className="tea-toolbar">
          <label className="tea-field">
            <span className="tea-label">Window</span>
            <select className="tea-select" value={days} onChange={(e) => setDays(Number(e.target.value))}>
              <option value={1}>Today</option>
              <option value={2}>Last 2 days</option>
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
              <option value={180}>Last 180 days</option>
              <option value={365}>Last year</option>
            </select>
          </label>

          <label className="tea-field">
            <span className="tea-label">State</span>
            <select className="tea-select" value={state} onChange={(e) => setState(e.target.value)}>
              <option>All States</option>
              <option>Louisiana</option>
              <option>Texas</option>
              <option>Hawaii</option>
            </select>
          </label>

          <label className="tea-field">
            <span className="tea-label">Team</span>
            <select className={`tea-select${team ? ' is-team' : ''}`} value={team} onChange={(e) => chooseTeam(e.target.value)}>
              <option value="">Everyone</option>
              {Object.keys(TEAMS).map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>

          <label className={`tea-field${team ? ' is-muted' : ''}`}
            title="Matches the record's creator, and anyone who typed a score in this window">
            <span className="tea-label">Person contains</span>
            <input
              className="tea-input"
              value={enteredBy}
              onChange={(e) => setEnteredBy(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !team) fetchData(); }}
              placeholder={team ? 'team selected' : 'e.g. Tracy — then Run'}
              disabled={!!team}
            />
          </label>

          <div className="tea-actions">
            <button className="tea-btn tea-btn--primary" onClick={fetchData} disabled={loading || busy}>
              <RefreshCw size={15} className={loading ? 'hf-spinner' : undefined} /> Run audit
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div style={{ ...card, borderColor: 'rgba(239,68,68,0.4)', marginBottom: '1rem' }}>
          <div style={{ color: '#FCA5A5', fontSize: '0.85rem', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <ShieldAlert size={16} /> {error}
          </div>
        </div>
      )}

      {loading && !data && (
        <div style={{ ...card, textAlign: 'center', padding: '3rem 2rem' }}>
          <RefreshCw size={24} className="hf-spinner" style={{ marginBottom: '0.75rem' }} />
          <div style={{ ...muted, fontSize: '0.9rem' }}>Reading test records from Salesforce…</div>
        </div>
      )}

      {data && (
        <>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '1rem' }}>
            <Stat label="Records audited" value={data.totals.records.toLocaleString()} sub={`${data.totals.candidates.toLocaleString()} candidates`} />
            <Stat label="With errors" value={data.totals.withErrors.toLocaleString()} tone="#EF4444"
              sub={data.totals.records ? `${((100 * data.totals.withErrors) / data.totals.records).toFixed(1)}% of records` : ''} />
            <Stat label="With warnings" value={data.totals.withWarnings.toLocaleString()} tone="#FBBF24" />
            <Stat label="Clean on the rules" value={data.totals.clean.toLocaleString()} tone="#4ADE80"
              sub={data.totals.records ? `${((100 * data.totals.clean) / data.totals.records).toFixed(1)}%` : ''} />
            <Stat label="Score on file" value={`${data.totals.records ? ((100 * data.totals.withScore) / data.totals.records).toFixed(0) : 0}%`}
              sub={`${data.totals.withDocument.toLocaleString()} have a score report`} />
            <Stat label="Scored in window" value={(data.totals.scoredInWindow ?? 0).toLocaleString()} tone="#93C5FD"
              sub="score or date actually typed in this window" />
          </div>

          {/* ── accuracy against the score reports ── */}
          <div style={{ ...card, marginBottom: '1rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
              <FileText size={16} style={{ color: '#818CF8' }} />
              <div style={{ fontSize: '0.95rem', fontWeight: 600, color: '#E0E7FF' }}>Accuracy against the score reports</div>
            </div>

            <div style={{ ...muted, fontSize: '0.8rem', marginBottom: '0.9rem', lineHeight: 1.5, maxWidth: '78ch' }}>
              The numbers above come from Salesforce alone. They catch values that cannot be right, but never a
              score that is plausible and simply wrong. Only reading the report catches that.
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.6rem', marginBottom: '1rem' }}>
              <button className="tea-btn tea-btn--primary tea-btn--sm" onClick={() => runVerification('all')}
                disabled={busy || !(data.verifyQueue?.all || []).length}>
                <FileText size={14} /> Read {Math.min(RUN_SIZE.all, (data.verifyQueue?.all || []).length)} unread report{(data.verifyQueue?.all || []).length === 1 ? '' : 's'}
              </button>
              <button className="tea-btn tea-btn--sm" onClick={() => runVerification('sample')}
                disabled={busy || !(data.verifyQueue?.sample || []).length}>
                <Shuffle size={14} /> Spot-check {Math.min(RUN_SIZE.sample, (data.verifyQueue?.sample || []).length)} at random
              </button>
              <button className="tea-btn tea-btn--sm" onClick={() => runVerification('suspect')}
                disabled={busy || !(data.verifyQueue?.suspect || []).length}>
                <AlertTriangle size={14} /> Confirm {Math.min(RUN_SIZE.suspect, (data.verifyQueue?.suspect || []).length)} flagged
              </button>
              {busy && (
                <button className="tea-btn tea-btn--sm tea-btn--quiet" onClick={() => { cancelled.current = true; setRun((r) => ({ ...r, finished: true })); }}>
                  Stop
                </button>
              )}
              {!busy && (data.verifyQueue?.alreadyChecked || 0) > 0 && (
                <button className="tea-btn tea-btn--sm tea-btn--quiet" onClick={() => runVerification('all', { force: true })}
                  title="Ignore stored results and read every report again">
                  <RefreshCw size={12} /> Re-read all
                </button>
              )}
              <div style={{ ...muted, fontSize: '0.8rem', alignSelf: 'center' }}>
                {(data.verifyQueue?.alreadyChecked || 0) > 0 && (
                  <span style={{ color: '#93C5FD' }}>{data.verifyQueue.alreadyChecked} already checked</span>
                )}
                {(data.verifyQueue?.alreadyChecked || 0) > 0 && run ? ' · ' : ''}
                {run && (
                  <>
                    {busy ? <RefreshCw size={12} className="hf-spinner" style={{ verticalAlign: 'middle', marginRight: 4 }} /> : null}
                    {run.done} of {run.total} {run.mode === 'sample' ? 'random ' : run.mode === 'all' ? 'new ' : 'flagged '}records read
                  </>
                )}
                {!run && (data.verifyQueue?.alreadyChecked || 0) > 0 && !(data.verifyQueue?.all || []).length && (
                  <span> · nothing new to read</span>
                )}
              </div>
            </div>

            {busy && sampleAcc.counted === 0 ? (
              <div style={{ ...muted, fontSize: '0.85rem', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <RefreshCw size={14} className="hf-spinner" />
                Reading all {run.total} score report{run.total > 1 ? 's' : ''} for this window…
              </div>
            ) : sampleAcc.counted > 0 ? (
              <>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.9rem', alignItems: 'center' }}>
                  <div style={{ fontSize: '2.4rem', fontWeight: 700, color: sampleAcc.pct >= 95 ? '#4ADE80' : sampleAcc.pct >= 80 ? '#FBBF24' : '#EF4444' }}>
                    {sampleAcc.pct.toFixed(0)}%
                  </div>
                  <div style={{ ...muted, fontSize: '0.85rem', lineHeight: 1.5 }}>
                    <div style={{ color: '#C7D2FE' }}>
                      {sampleAcc.matched} of {sampleAcc.counted}{isCensus ? '' : ' randomly sampled'} records with a recorded score match their report
                    </div>
                    <div style={{ fontSize: '0.78rem' }}>
                      {isCensus
                        ? `Every record in this window with a report on file was read — this is a count, not an estimate.`
                        : `Drawn from the ${(data.verifyQueue?.cleanTotal || 0).toLocaleString()} records the rules found nothing wrong with, so this estimates accuracy across the whole window.`}
                    </div>
                    <div style={{ fontSize: '0.78rem', marginTop: '0.2rem' }}>
                      {sampleAcc.missing > 0 && (
                        <span style={{ color: '#FDE68A' }}>
                          {sampleAcc.missing} more had a score on the report but nothing recorded against the test.{' '}
                        </span>
                      )}
                      {sampleAcc.inconclusive > 0 && (
                        <span>{sampleAcc.inconclusive} could not be judged — no report, unreadable, or only partly on file.</span>
                      )}
                    </div>
                  </div>
                </div>

                {sampleProblems.length > 0 && (
                  <div style={{ marginTop: '0.9rem' }}>
                    <div style={{ fontSize: '0.82rem', color: '#FCA5A5', marginBottom: '0.4rem' }}>
                      {sampleProblems.length} record{sampleProblems.length > 1 ? 's' : ''} that disagree with the report — none of which the rules could have caught:
                    </div>
                    {sampleProblems.map((r) => (
                      <div key={r.testId} style={{ fontSize: '0.78rem', color: '#C7D2FE', marginBottom: '0.3rem' }}>
                        <a href={`${SF_BASE}/${r.testId}`} target="_blank" rel="noreferrer" style={{ color: '#818CF8', textDecoration: 'none' }}>
                          {r.testRecordName}
                        </a>{' '}
                        {r.candidate} · {creditOf(r)} — {fmtDates(r.pdfVerification.detail)}
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <div style={{ ...muted, fontSize: '0.82rem' }}>
                {(data.verifyQueue?.all || []).length > AUTO_VERIFY_MAX
                  ? <>This window has {(data.verifyQueue?.verifiableTotal || 0).toLocaleString()} records with reports on file — too many to read automatically. <strong style={{ color: '#C7D2FE' }}>Spot-check at random</strong> estimates accuracy across the window; <strong style={{ color: '#C7D2FE' }}>Read all</strong> checks every one, which takes longer and costs more.</>
                  : <>Nothing to read for this window — no records have a score report on file.</>}
              </div>
            )}

            {suspectResults.length > 0 && (
              <div style={{ marginTop: '1rem', paddingTop: '0.8rem', borderTop: '1px dashed rgba(129,140,248,0.2)' }}>
                <div style={{ ...muted, fontSize: '0.78rem' }}>
                  Separately, {suspectResults.filter((r) => r.pdfVerification?.status === 'mismatch').length} of{' '}
                  {suspectResults.length} flagged records were confirmed wrong against their report. This is a
                  confirmation rate, not an accuracy rate — those records were chosen because they already looked wrong.
                </div>
              </div>
            )}
          </div>

          {/* ── per-person scorecard ── */}
          {(() => {
            const roster = team && TEAMS[team] ? TEAMS[team].members : null;
            // Every member gets a row, even with nothing entered today — a
            // supervisor needs to see who did no work as much as who did.
            const base = roster
              ? roster.map((name) =>
                  data.scorecard.find((p) => p.name === name) || {
                    name, records: 0, errors: 0, warnings: 0, clean: 0, withScore: 0, proven: 0,
                    cleanRate: 0, errorRate: 0, scoreCoverage: 0, provenRate: 0,
                  }
                )
              : data.scorecard;
            const scorecard = applyVerdicts(base);
            const teamTotals = roster && {
              records: scorecard.reduce((n, p) => n + p.records, 0),
              errors: scorecard.reduce((n, p) => n + p.errors, 0),
              warnings: scorecard.reduce((n, p) => n + p.warnings, 0),
              clean: scorecard.reduce((n, p) => n + p.clean, 0),
              reportProblems: scorecard.reduce((n, p) => n + p.reportProblems, 0),
              reportChecked: scorecard.reduce((n, p) => n + p.reportChecked, 0),
            };
            return (
          <div style={{ ...card, marginBottom: '1rem' }}>
            {roster ? (
              <div style={{ marginBottom: '0.9rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: '0.5rem' }}>
                  <div style={{ fontSize: '1.05rem', fontWeight: 600, color: '#E0E7FF' }}>{team}</div>
                  <div style={{ ...muted, fontSize: '0.78rem' }}>Supervisor · {TEAMS[team].supervisor}</div>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1.25rem', marginTop: '0.55rem', fontSize: '0.82rem' }}>
                  <span style={{ color: '#C7D2FE' }}><strong>{teamTotals.records}</strong> records {days === 1 ? 'today' : `in ${days} days`}</span>
                  <span style={{ color: teamTotals.errors ? '#FCA5A5' : 'rgba(199,210,254,0.6)' }}><strong>{teamTotals.errors}</strong> rule errors</span>
                  <span style={{ color: teamTotals.reportProblems ? '#FCA5A5' : 'rgba(199,210,254,0.6)' }}>
                    <strong>{teamTotals.reportProblems}</strong> wrong against the report
                    {teamTotals.reportChecked ? ` (${teamTotals.reportChecked} checked)` : ''}
                  </span>
                  <span style={{ color: teamTotals.warnings ? '#FDE68A' : 'rgba(199,210,254,0.6)' }}><strong>{teamTotals.warnings}</strong> warnings</span>
                  <span style={{ color: teamTotals.records && teamTotals.clean === teamTotals.records ? '#4ADE80' : '#FBBF24' }}>
                    <strong>{teamTotals.records ? ((100 * teamTotals.clean) / teamTotals.records).toFixed(0) : '—'}%</strong> clean
                  </span>
                  {sampleAcc.counted > 0 && (
                    <span style={{ color: sampleAcc.pct >= 95 ? '#4ADE80' : sampleAcc.pct >= 80 ? '#FBBF24' : '#EF4444' }}>
                      <strong>{sampleAcc.pct.toFixed(0)}%</strong> match their score report ({sampleAcc.matched}/{sampleAcc.counted})
                    </span>
                  )}
                </div>
              </div>
            ) : (
              <div style={{ fontSize: '0.95rem', fontWeight: 600, color: '#E0E7FF', marginBottom: '0.75rem' }}>Scorecard by enterer</div>
            )}
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                <thead>
                  <tr style={{ ...muted, textAlign: 'left', borderBottom: '1px solid rgba(129,140,248,0.25)' }}>
                    <th style={{ padding: '0.5rem 0.6rem' }}>Entered by</th>
                    <th style={{ padding: '0.5rem 0.6rem', textAlign: 'right' }}>Records</th>
                    <th style={{ padding: '0.5rem 0.6rem', textAlign: 'right' }}>Clean</th>
                    <th style={{ padding: '0.5rem 0.6rem', textAlign: 'right' }}>Rule errors</th>
                    <th style={{ padding: '0.5rem 0.6rem', textAlign: 'right' }} title="Records that disagree with their score report, of those checked">Wrong vs report</th>
                    <th style={{ padding: '0.5rem 0.6rem', textAlign: 'right' }}>Warnings</th>
                    <th style={{ padding: '0.5rem 0.6rem', textAlign: 'right' }}>Score filled</th>
                    <th style={{ padding: '0.5rem 0.6rem', textAlign: 'right' }}>Proven author</th>
                  </tr>
                </thead>
                <tbody>
                  {scorecard.map((p) => (
                    <tr key={p.name} onClick={() => setPersonFilter(personFilter === p.name ? '' : p.name)}
                      style={{
                        borderBottom: '1px solid rgba(129,140,248,0.1)', cursor: 'pointer',
                        background: personFilter === p.name ? 'rgba(129,140,248,0.12)' : 'transparent',
                      }}>
                      <td style={{ padding: '0.5rem 0.6rem', color: p.records ? '#E0E7FF' : 'rgba(224,231,255,0.5)' }}>
                        {p.name}
                        {!roster && p.records < 20 && <span style={{ ...muted, fontSize: '0.7rem' }}> · low volume</span>}
                        {roster && !p.records && <span style={{ ...muted, fontSize: '0.7rem' }}> · nothing entered</span>}
                      </td>
                      <td style={{ padding: '0.5rem 0.6rem', textAlign: 'right', color: '#C7D2FE' }}>{p.records.toLocaleString()}</td>
                      <td style={{ padding: '0.5rem 0.6rem', textAlign: 'right', fontWeight: 600, color: p.cleanRate >= 95 ? '#4ADE80' : p.cleanRate >= 85 ? '#FBBF24' : '#EF4444' }}>{p.cleanRate}%</td>
                      <td style={{ padding: '0.5rem 0.6rem', textAlign: 'right', color: p.errors ? '#FCA5A5' : 'rgba(199,210,254,0.45)' }}>{p.errors}</td>
                      <td style={{ padding: '0.5rem 0.6rem', textAlign: 'right', color: p.reportProblems ? '#FCA5A5' : p.reportChecked ? '#4ADE80' : 'rgba(199,210,254,0.35)' }}>
                        {p.reportChecked ? `${p.reportProblems} / ${p.reportChecked}` : '—'}
                      </td>
                      <td style={{ padding: '0.5rem 0.6rem', textAlign: 'right', color: p.warnings ? '#FDE68A' : 'rgba(199,210,254,0.45)' }}>{p.warnings}</td>
                      <td style={{ padding: '0.5rem 0.6rem', textAlign: 'right', ...muted }}>{p.scoreCoverage}%</td>
                      <td style={{ padding: '0.5rem 0.6rem', textAlign: 'right', color: p.proven ? '#C7D2FE' : 'rgba(199,210,254,0.35)' }}>
                        {p.proven ? `${p.proven}` : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ ...muted, fontSize: '0.72rem', marginTop: '0.6rem' }}>
              Click a row to filter the findings below. "Clean" means the rules found nothing <em>and</em>, where the
              report was read, it agreed. Each record is credited to whoever field history says set its score, falling
              back to the record's creator; "Proven author" counts how many rows are the former kind.
            </div>
          </div>
            );
          })()}

          {data.ruleTotals?.length > 0 && (
            <div style={{ ...card, marginBottom: '1rem' }}>
              <div style={{ fontSize: '0.95rem', fontWeight: 600, color: '#E0E7FF', marginBottom: '0.75rem' }}>Findings by type</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                {data.ruleTotals.map((r) => (
                  <div key={r.id} style={{
                    border: `1px solid ${SEVERITY_COLOR[r.severity]}55`, background: `${SEVERITY_COLOR[r.severity]}12`,
                    borderRadius: '0.5rem', padding: '0.5rem 0.7rem', fontSize: '0.78rem',
                  }}>
                    <span style={{ color: SEVERITY_COLOR[r.severity], fontWeight: 600 }}>{r.count}</span>
                    <span style={{ color: '#C7D2FE' }}> · {r.label}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div style={card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.75rem' }}>
              <div style={{ fontSize: '0.95rem', fontWeight: 600, color: '#E0E7FF' }}>
                Flagged records {personFilter && <span style={{ ...muted, fontWeight: 400 }}>· {personFilter}</span>}
              </div>
              <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                {['all', 'error', 'warn', 'verified'].map((s) => (
                  <button key={s} onClick={() => setSeverityFilter(s)}
                    className={`tea-btn tea-btn--sm tea-btn--quiet${severityFilter === s ? ' is-active' : ''}`}>
                    {s === 'all' ? 'All' : s === 'verified' ? 'PDF-checked' : SEVERITY_LABEL[s] + 's'}
                  </button>
                ))}
                {personFilter && (
                  <button onClick={() => setPersonFilter('')} className="tea-btn tea-btn--sm tea-btn--quiet">Clear person</button>
                )}
              </div>
            </div>

            {rows.length === 0 ? (
              <div style={{ ...muted, fontSize: '0.85rem', padding: '1.5rem', textAlign: 'center' }}>
                <CheckCircle2 size={20} style={{ color: '#4ADE80', marginBottom: '0.4rem' }} />
                <div>Nothing flagged for this filter.</div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', maxHeight: '38rem', overflowY: 'auto' }}>
                {rows.map((r) => (
                  <div key={r.testId} style={{
                    border: '1px solid rgba(129,140,248,0.18)', borderRadius: '0.55rem',
                    padding: '0.7rem 0.85rem', background: 'rgba(15, 12, 45, 0.4)',
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
                      <div>
                        <span style={{ color: '#E0E7FF', fontWeight: 600, fontSize: '0.87rem' }}>{r.candidate || '(no name)'}</span>
                        <span style={{ ...muted, fontSize: '0.78rem' }}>
                          {' '}· {r.testName || '(no test name)'}{r.praxisNumber != null ? ` #${r.praxisNumber}` : ''}
                          {r.score != null && ` · recorded ${r.score}`}
                        </span>
                      </div>
                      <div style={{ ...muted, fontSize: '0.74rem' }}>
                        {r.provenBy
                          ? <span style={{ color: '#93C5FD' }} title={`From Salesforce field history${r.scoreSetDate ? ' on ' + r.scoreSetDate : ''}`}>
                              {r.provenBy} · set {fmtDate(r.scoreSetDate || r.dateSetDate)}
                            </span>
                          : <span title="Record creator — not proof of who entered the value">{r.enteredBy} · created {fmtDate(r.createdDate)}</span>}
                        <a href={`${SF_BASE}/${r.testId}`} target="_blank" rel="noreferrer" style={{ color: '#818CF8', marginLeft: '0.5rem', textDecoration: 'none' }}>
                          {r.testRecordName} <ExternalLink size={11} style={{ verticalAlign: 'middle' }} />
                        </a>
                      </div>
                    </div>

                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem', marginTop: '0.5rem' }}>
                      {r.findings.map((f, i) => (
                        <div key={i} style={{
                          fontSize: '0.76rem', color: SEVERITY_COLOR[f.severity],
                          border: `1px solid ${SEVERITY_COLOR[f.severity]}44`, background: `${SEVERITY_COLOR[f.severity]}10`,
                          borderRadius: '0.4rem', padding: '0.25rem 0.5rem',
                        }} title={fmtDates(f.detail)}>{fmtDates(f.detail)}</div>
                      ))}
                    </div>

                    {r.pdfVerification && (
                      <div style={{
                        marginTop: '0.5rem', paddingTop: '0.5rem', borderTop: '1px dashed rgba(129,140,248,0.2)',
                        fontSize: '0.78rem', color: (VERDICT[r.pdfVerification.status] || VERDICT.error).color,
                      }}>
                        <strong>{(VERDICT[r.pdfVerification.status] || VERDICT.error).label}:</strong>{' '}
                        <span style={{ color: '#C7D2FE' }}>{fmtDates(r.pdfVerification.detail)}</span>
                        {r.pdfVerification.filesRead?.length > 0 && (
                          <div style={{ ...muted, fontSize: '0.71rem', marginTop: '0.3rem' }}>
                            Read {r.pdfVerification.filesRead.join(', ')}
                            {r.pdfVerification.documentsAvailable > r.pdfVerification.filesRead.length &&
                              ` (of ${r.pdfVerification.documentsAvailable} on file)`}
                          </div>
                        )}
                        {r.pdfVerification.verifiedAt && (
                          <div style={{ ...muted, fontSize: '0.71rem', marginTop: '0.15rem' }}>
                            Checked {fmtWhen(r.pdfVerification.verifiedAt)}
                            {r.pdfVerification.verifiedBy ? ` by ${r.pdfVerification.verifiedBy}` : ''}
                            {r.pdfVerification.cached ? ' · from record' : ''}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
            <div style={{ ...muted, fontSize: '0.72rem', marginTop: '0.6rem' }}>
              Showing {rows.length.toLocaleString()} of {(data.rows || []).length.toLocaleString()} flagged records. Clean records are not listed.
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default TestEntryAudit;
